import React, { useRef } from "react";
import { useState, useEffect, useContext } from "react";
import { Button, Form, Alert, Table, Modal } from "react-bootstrap";

import PortalService from '../../../services/portal.service';
import StateContext from '../../../context/State';
import getArrayBufferFromFile from '../../../../../util/getArrayBufferFromFile';
import getIconForFileType from '../../../../../util/getIconForFileType';
import { DocumentsFromLawyerResponse, FileUploadModel, FileInfoModel } from "../../../types";
import { RPWDataReader, getDataReader } from "../../../../../livedataFuncs";
import { useAppSelector } from "../../../../../redux/hooks";
import { lddStateObject } from "../../../../../redux/reducer";
import { lddGlobalSessionInfo } from "../../../../navigationTypes";
import { getIncludeFirmEmail } from "../../../utils/includeFirmEmail";
import languageMap from "../../../../../../../ClientPortal/src/langs.json";


// ─────────────────────────────────────────────────────────────────────────────
// Document-share email throttle window.
// At most ONE "documents shared" email is sent to the client per file within
// this interval; additional uploads inside the window are batched (no extra
// email). The most-recent send time is read from the existing email log, so
// this needs no database / schema changes.
//
// 👉 LOCAL TESTING: lower this to verify the throttle without waiting 30 min,
//    e.g. `2 * 60 * 1000` for 2 minutes.
// ─────────────────────────────────────────────────────────────────────────────
const DOC_SHARE_EMAIL_THROTTLE_MS = 30 * 60 * 1000; // 30 minutes

const UploadDocumentsOnDashboardComponent = () => {

    const { state, bumpEmailLogRefresh } = useContext(StateContext);
    const portalService = new PortalService(state);
    const sessionInfo: lddGlobalSessionInfo = useAppSelector((s) => s.SessionInfo);
    const [sharedDocuments, setSharedDocuments] = useState<DocumentsFromLawyerResponse>(null);
    const [fileToBeUploaded, setFileToBeUploaded] = useState<FileUploadModel>(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const livedataRootContainer = useAppSelector((state: lddStateObject) => state.currentNavigation.livedataRootContainer);
    let DataReaderObj: RPWDataReader;
    DataReaderObj = getDataReader(livedataRootContainer.dataRoot);

    const aRef = useRef(null);

    // eslint-disable-next-line
    useEffect(() => { getAllSharedDocuments() }, [])

    const getAllSharedDocuments = async (): Promise<void> => {
        try {
            const sharedDocumentsResponse: DocumentsFromLawyerResponse = await portalService.getClientPortalDocuments(state.portalId.toString())
            if (sharedDocumentsResponse) { setSharedDocuments(sharedDocumentsResponse) }
        } catch (e) {
            console.log('Unable to retreive shared documents: ', e)
        }
    }

    const handleDocumentClicked = async (docName: string): Promise<void> => {
        try {
            await portalService.getClientPortalDocumentFromLaywer(state.portalId.toString(), docName);
        } catch (e) {
            alert(e);
        }
    }

    // Returns true if a document-share email has already gone out for this file
    // within DOC_SHARE_EMAIL_THROTTLE_MS. Source of truth is the existing email
    // log (rowKey encodes the send time as `${appId}-${ms}`); document-share rows
    // are identified by their localized subject line. No DB / schema changes.
    // Fails open: if the log can't be read, we do NOT suppress the notification.
    const wasDocumentShareEmailSentRecently = async (): Promise<boolean> => {
        try {
            const result: any = await portalService.fetchEmailLogs(state.appId);
            if (result?.status !== 'success' || !Array.isArray(result.data)) {
                return false;
            }
            const docShareSubjects: string[] = Object.values(languageMap.documentShareNotificationSubject);
            const lastSentMs = result.data
                .filter((log: any) => docShareSubjects.includes(log.Subject))
                .map((log: any) => Number(log.rowKey?.split('-')[1]))
                .filter((ms: number) => Number.isFinite(ms))
                .reduce((max: number, ms: number) => Math.max(max, ms), 0);
            return lastSentMs > 0 && (Date.now() - lastSentMs) < DOC_SHARE_EMAIL_THROTTLE_MS;
        } catch (e) {
            console.log('Document-share throttle check failed; sending notification anyway:', e);
            return false;
        }
    };

    const sendDocumentShareEmailsToClients = async (): Promise<void> => {
        // Throttle: send at most one document-share email per file per window.
        // Runs once here (before the per-recipient loop below) so the whole batch
        // is either sent to every client or suppressed together.
        if (await wasDocumentShareEmailSentRecently()) {
            console.log('Document-share email suppressed: one was already sent within the throttle window.');
            return;
        }

        let isNotaryFirm = false;
        let language = 'EN';
        try {
            const fileInfoData: FileInfoModel = await portalService.getFileInfo();
            isNotaryFirm = Boolean(fileInfoData?.file?.isNotaryFirm);
            language = DataReaderObj.getLanguage() || 'EN';
        } catch (e) {
            console.log('getFileInfo for document share email:', e);
        }

        const subjectLine =
            languageMap.documentShareNotificationSubject[language] ??
            languageMap.documentShareNotificationSubject.EN;

        const textBodyPlain = isNotaryFirm
            ? 'Your notary has shared one or more files with you. Please access ClientConnect on the web to obtain these files.'
            : 'Your lawyer has shared one or more files with you. Please access ClientConnect on the web to obtain these files.';

        const portal: any = await portalService.getPortalByAppId(state.appId);
        if (!portal?.length) {
            return;
        }
        const portalId = portal[0].Id;
        const province = await portalService.getProvince();
        const code = await portalService.getShortCode(portalId);
        const codeVal = code?.shortCode;
        if (!codeVal) {
            return;
        }

        const emailInfos: string[] = DataReaderObj.getClientContactInfo(province, true) ?? [];
        if (emailInfos.length === 0) {
            console.log('Document uploaded; no client email addresses on file to notify.');
            return;
        }

        const firmName = DataReaderObj.getFirmName();
        const firmIncluded = await getIncludeFirmEmail(sessionInfo?.sid ?? '');
        const firmContactEmail = DataReaderObj.getFirmIncludedEmail();

        for (const email of emailInfos) {
            const emailData = {
                appId: state.appId,
                from: 'clientconnect@rts.lawyerdonedeal.com',
                to: email,
                subject: subjectLine,
                message: textBodyPlain,
                ccEmail: '',
                bccEmail: firmIncluded ? firmContactEmail : '',
                user: '',
                type: 'documentShare',
                shortCode: codeVal,
                lawyerName: firmName
            };
            try {
                // Send first, then log — a failed send should not leave a log row
                // that the throttle would treat as a recent send and suppress the
                // next (legitimate) notification.
                await portalService.sendEmail(state.appId, emailData, isNotaryFirm, language);
                await portalService.logEmail(state.appId, emailData);
            } catch (error) {
                console.error(`Failed to send document share email to ${email}`, error);
            }
        }
    };

    const handleUploadDocument = async (): Promise<void> => {
        if (!fileToBeUploaded) {
            alert('You must upload a valid document before submitting.');
            return;
        }
        if (isSubmitting) {
            return;
        }
        setIsSubmitting(true);
        try {
            const arrayBuffer: any = await getArrayBufferFromFile(fileToBeUploaded.uploadedFile);
            await portalService.uploadDocument(arrayBuffer, state.portalId, state.appId, fileToBeUploaded.fileName, 'CPL');
            setFileToBeUploaded(null);
            if (aRef.current) {
                aRef.current.value = null;

            }
            getAllSharedDocuments();
            await sendDocumentShareEmailsToClients();
            bumpEmailLogRefresh();
        } catch (e) {
            console.log(e);
            alert('Problem uploading this document.')
        } finally {
            setIsSubmitting(false);
        }
    }

    const handleFileUploadComponent = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        const files = Array.from(e.target.files);
        let uploadedFile: File;
        if (files) {
            if (files[0]) {
                uploadedFile = files[0];
                const fileName: string = uploadedFile.name;
                setFileToBeUploaded({ uploadedFile, fileName });
            }
        }
    }

    const handleDocumentDelete = (docName: string) => {
        setSelectedDocument(docName);
        setShowModal(true);
    };

    const handleConfirmDelete = (docName: string) => {
        confirmDocumentDelete(docName)
        setShowModal(false);
    };

    const handleCancelDelete = () => {
        setShowModal(false);
    };

    const confirmDocumentDelete = async (docName: string): Promise<void> => {
        try {
            const resp = await portalService.deleteClientPortalDocumentFromLawyer(state.portalId.toString(), docName);
            if (resp.data.success){
                setSharedDocuments(prevDocuments => ({
                    Docs: prevDocuments.Docs.filter((d) => d.Name.split("/").at(-1) !== docName)
                }));
            }
            else{
                alert(resp.data.code)
            }
        } catch (e) {
            console.log(e)
            alert('Could not delete document. Please refresh the page and try again.')
        }
    }

    return (
        <>
            <Form.Group>
                <Form.Label>Upload Document</Form.Label>
                <Form.Control
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => { handleFileUploadComponent(e) }}
                    className="form-upload"
                    type="file"
                    ref={aRef}
                />
            </Form.Group>

            <Button onClick={() => handleUploadDocument()} disabled={isSubmitting}>
                {isSubmitting ? 'Submitting…' : 'Submit'}
            </Button>

            <br /> <br />
            
            <Form.Label>All Documents</Form.Label>

            {
                sharedDocuments && sharedDocuments.Docs.length !== 0 ?
                    <Table striped hover>
                        <tbody>
                            {
                                sharedDocuments.Docs.map((doc) => {
                                    const tempArray = (doc.Name).split("/");
                                    const className = getIconForFileType(tempArray.at(-1));
                                    return (
                                        <tr key={doc.Name + '-document-shared'}>
                                            <td onClick={() => { handleDocumentClicked(tempArray.at(-1)) }} >
                                                <i className={className} style={{ padding: 5 }} />
                                                {tempArray.at(-1)}
                                            </td>
                                            <td className="text-right">
                                                <i onClick={ () => handleDocumentDelete(tempArray.at(-1)) } className="fa-regular fa-trash-can"></i>
                                            </td>
                                        </tr>
                                    )
                                })
                            }
                        </tbody>
                    </Table>
                    :
                    <Alert>No documents added.</Alert>
            }

                    <Modal show={showModal} onHide={() => setShowModal(false)}>
                        <Modal.Header closeButton>
                            <Modal.Title>Delete Document</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            "{selectedDocument}" will no longer be shared with the client. Your client may have already downloaded this document. Are you sure you would like to remove it from ClientConnect?
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleCancelDelete}>
                                Cancel
                            </Button>
                            <Button variant="primary" onClick={() => handleConfirmDelete(selectedDocument)}>
                                Confirm
                            </Button>
                        </Modal.Footer>
                    </Modal>
        </>
    )
}

export default UploadDocumentsOnDashboardComponent;
